import * as React from 'react';
import {Link as RouterLink} from 'react-router-dom';

import classnames from 'classnames';

import {ActionableHeader} from '../actionable-header/actionable-header';
import {Card} from '../card/card';
import {Dialog} from '../dialog/dialog';
import {getDripEditionStatusInfo, getEditionStatusInfo} from '../edition/get-edition-status-info';
// @ts-expect-error - imported from .js/.jsx file
import {ArchiveIcon} from '../icon/svg/archive-icon';
// @ts-expect-error - imported from .js/.jsx file
import {EyeIcon} from '../icon/svg/eye-icon';
// @ts-expect-error - imported from .js/.jsx file
import {TrashIcon} from '../icon/svg/trash-icon';
import {Link} from '../link/link';
import {Metric} from '../metrics/metric';
import {Percentage} from '../percentage/percentage';
import {ReadOnlyText} from '../sms-campaigns/read-only-text';
import {FormattedTimestamp} from '../timestamp/formatted-timestamp';
import {TwoLine} from '../two-line/two-line';

import type {Edition} from './types';

import styles from './latest-outreach-list-item.module.css';

type Props = {
    edition: Edition;
    onView: (url: string) => void;
    archive?: {
        onArchive: (id: string) => void;
        shouldConfirm: boolean;
    };
    isDripEdition?: boolean;
    hasNoBorder?: boolean;
    hideAudience?: boolean;
};

export function LatestOutreachListItem(props: Props) {
    const [isConfirmingRemove, setIsConfirmingRemove] = React.useState<boolean>(false);

    const options = [
        {
            label: 'View',
            variant: 'secondary',
            icon: <EyeIcon wrapWithDiv={false} size={18} />,
            handleSelect: () => {
                props.onView(props.edition.htmlUrlPath);
            },
        },
    ];

    const actionMenuRef = React.useRef(null);

    if (props.archive) {
        options.push({
            label: props.archive.shouldConfirm ? 'Remove' : 'Archive',
            variant: 'destructive',
            icon: props.archive.shouldConfirm ? <TrashIcon size={16} /> : <ArchiveIcon size={16} />,
            handleSelect: () => {
                if (props.archive && props.archive.shouldConfirm) {
                    setIsConfirmingRemove(true);
                } else {
                    props.archive?.onArchive?.(props.edition.campaign.id);
                }
            },
        });
    }

    function getTimestamp() {
        if (props.edition.scheduledTime) {
            return (
                <FormattedTimestamp
                    format={FormattedTimestamp.formats.longMonthDayYearWithTime}
                    datetime={props.edition.scheduledTime}
                />
            );
        }

        if (props.edition.modifiedTime) {
            return (
                <div className='flex'>
                    <div className='mr-4'>Last modified</div>
                    <FormattedTimestamp
                        format={FormattedTimestamp.formats.longMonthDayYearWithTime}
                        datetime={props.edition.modifiedTime}
                    />
                </div>
            );
        }

        return undefined;
    }

    let thumbnail;
    if (props.edition.content) {
        thumbnail = (
            <ReadOnlyText
                includeIcon={true}
                message={props.edition.content}
                optOutText={props.edition.optOutText}
                isFullWidth={true}
            />
        );
    } else if (props.edition.emailTemplate) {
        thumbnail = (
            <div
                style={{backgroundColor: props.edition.backgroundColor}}
                className={styles['image-container']}
            >
                {/* @ts-expect-error */}
                <img className='full-width' src={props.edition.emailTemplate.thumbnailUrl} />
            </div>
        );
    }

    const mainContainerStyle = classnames({
        [styles['main-container']]: true,
        [styles['main-container--column']]: props.edition.content,
    });

    return (
        <div>
            <Card hasBorder={!props.hasNoBorder} hasShadow={false} fitContent={false}>
                {isConfirmingRemove && props.archive ? (
                    <Dialog
                        anchor={actionMenuRef.current}
                        question={`Are you sure you want to remove ${props.edition.name}? This cannot be undone.`}
                        onPrimary={() => {
                            if (props.archive) {
                                props.archive.onArchive(props.edition.id);
                                setIsConfirmingRemove(false);
                            } else {
                                setIsConfirmingRemove(false);
                            }
                        }}
                        onSecondary={() => {
                            setIsConfirmingRemove(false);
                        }}
                        isWarning={true}
                        primaryText='Remove'
                        secondaryText='Cancel'
                        location='bottom'
                    />
                ) : null}
                <div>
                    <ActionableHeader
                        isArchived={false}
                        name={props.edition.name}
                        to={props.edition.htmlUrlPath}
                        metadataPrimary={props.isDripEdition ? undefined : getTimestamp()}
                        status={
                            (props.isDripEdition
                                ? getDripEditionStatusInfo(props.edition.status)
                                : getEditionStatusInfo(props.edition.status)) || undefined
                        }
                        secondaryLink={
                            props.isDripEdition
                                ? undefined
                                : {
                                      text: 'Performance',
                                      onClick: () => {
                                          props.onView(`${props.edition.htmlUrlPath}/engagement`);
                                      },
                                  }
                        }
                        // @ts-expect-error
                        options={options}
                        getActionMenuRef={(ref) => {
                            // @ts-expect-error
                            actionMenuRef.current = ref;
                        }}
                    />
                    <div className={mainContainerStyle}>
                        {thumbnail}
                        <div
                            className={
                                props.isDripEdition
                                    ? styles['details-container--drip-sequence']
                                    : styles['details-container']
                            }
                        >
                            <div>
                                {props.edition.emailTemplate ? (
                                    <TwoLine
                                        isLarge={true}
                                        topRow='Subject'
                                        bottomRow={
                                            props.edition.emailTemplate.subject ? (
                                                props.edition.emailTemplate.subject
                                            ) : (
                                                <span>
                                                    <i>No subject</i>
                                                </span>
                                            )
                                        }
                                    />
                                ) : undefined}
                            </div>
                            {!props.isDripEdition ? (
                                <div className={styles['two-line-container']}>
                                    {props.edition.from ? (
                                        <TwoLine
                                            isLarge={true}
                                            topRow='From'
                                            bottomRow={props.edition.from.emailAddress.formatted}
                                        />
                                    ) : undefined}
                                    {props.edition.audience && !props.hideAudience ? (
                                        <TwoLine
                                            isLarge={true}
                                            topRow='To'
                                            bottomRow={
                                                <Link
                                                    variant='primary'
                                                    size='normal'
                                                    // @ts-expect-error
                                                    as={RouterLink}
                                                    to={props.edition.audience.htmlUrlPath}
                                                >
                                                    {props.edition.audience.name}
                                                </Link>
                                            }
                                        />
                                    ) : undefined}
                                </div>
                            ) : undefined}
                            <div>
                                <div className={styles['two-line-container']}>
                                    {props.edition.engagementStats &&
                                        Array.isArray(props.edition.engagementStats) &&
                                        props.edition.engagementStats.map((stat) => (
                                            <div key={stat.title}>
                                                <Metric
                                                    title={stat.title}
                                                    value={
                                                        <div className={styles['large-font']}>
                                                            {stat.format.type === 'percent' ? (
                                                                // @ts-expect-error
                                                                <Percentage value={stat.value} />
                                                            ) : (
                                                                <>
                                                                    {stat.value &&
                                                                    stat.format.useLocale
                                                                        ? stat.value.toLocaleString()
                                                                        : stat.value}
                                                                </>
                                                            )}
                                                        </div>
                                                    }
                                                />
                                            </div>
                                        ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    );
}
